/**
 * Hero Above the Fold JavaScript
 * Optimized for LCP + robustness
 */
(function () {
  'use strict';

  // ---- Init ---------------------------------------------------------------
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', initHeroATF)
    : initHeroATF();

  function initHeroATF() {
    document.querySelectorAll('.hero-atf').forEach(initSection);
    setInitialBreakpoint();
    // Throttled (rAF) resize handler
    window.addEventListener('resize', onResize, { passive: true });
  }

  function initSection(section) {
    section.dataset.loading = 'true';
    annotateSequential(section);
    initImageLoading(section);
    initCTAInteractions(section);
    initKeyboardNavigation(section);
  }

  // ---- Image Loading (LCP-first) -----------------------------------------
  function initImageLoading(section) {
    const img = section.querySelector('.hero-lcp-img');

    // Prefer the visible <img> (LCP)
    if (img) {
      const finalize = () => {
        section.dataset.loaded = 'true';
        section.classList.add('hero-atf--loaded');
        section.removeAttribute('data-loading');
        requestAnimationFrame(() => runSequentialAnimation(section));
      };

      if (img.complete && img.naturalWidth > 0) {
        // already loaded from cache/preload
        finalize();
      } else {
        img.addEventListener('load', finalize, { once: true });
        img.addEventListener('error', finalize, { once: true });
        // Safety timeout (slow networks)
        setTimeout(finalize, 3000);
      }
      return;
    }

    // Fallback: kein <img> vorhanden → CSS-Background laden (Legacy-Fall)
    const styles = getComputedStyle(section);
    let bg = styles.getPropertyValue('--hero-bg-desktop').trim()
      || styles.getPropertyValue('--hero-bg-mobile').trim()
      || styles.backgroundImage;

    const match = bg && bg.match(/url\(['"]?([^'"]+)['"]?\)/);
    const finalize = () => {
      section.dataset.loaded = 'true';
      section.classList.add('hero-atf--loaded');
      section.removeAttribute('data-loading');
      requestAnimationFrame(() => runSequentialAnimation(section));
    };

    if (!match || !match[1]) {
      finalize();
      return;
    }

    const preload = new Image();
    preload.onload = finalize;
    preload.onerror = finalize;
    setTimeout(finalize, 3000);
    preload.src = match[1];
  }

  // ---- Sequential Animations ---------------------------------------------
  function annotateSequential(section) {
    const order = [
      section.querySelector('.hero-atf__headlines'),
      section.querySelector('.hero-atf__usp-block'),
      section.querySelector('.hero-atf__ctas'),
      section.querySelector('.hero-atf__review'),
    ].filter(Boolean);

    order.forEach((el, i) => (el.dataset.animationOrder = String(i + 1)));
  }

  function runSequentialAnimation(section) {
    // Wenn der Shop eine eigene Animationsfunktion bereitstellt, nutze sie:
    if (typeof window.startSequentialAnimation === 'function') {
      try { window.startSequentialAnimation(section); return; }
      catch (e) { console.warn('startSequentialAnimation error:', e); }
    }

    // Default, barrierearm + performant:
    const reduce =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const items = Array.from(
      section.querySelectorAll('[data-animation-order]')
    ).sort((a, b) => (a.dataset.animationOrder || 0) - (b.dataset.animationOrder || 0));

    if (reduce) {
      items.forEach((el) => {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
      return;
    }

    let delay = 0;
    items.forEach((el) => {
      el.style.willChange = 'opacity, transform';
      el.style.transition = 'opacity .4s ease, transform .4s ease';
      el.style.opacity = '0';
      el.style.transform = 'translateY(8px)';
      setTimeout(() => {
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      }, delay);
      delay += 120;
    });
  }

  // ---- CTA Interactions ---------------------------------------------------
  function initCTAInteractions(section) {
    const primary = section.querySelector('.hero-atf__primary-cta');
    const secondary = section.querySelector('.hero-atf__secondary-cta');

    if (primary) {
      primary.addEventListener(
        'click',
        (e) => {
          createRippleEffect(e, primary);
          trackCTAClick('primary', primary.href);
        },
        { passive: true }
      );
    }
    if (secondary) {
      secondary.addEventListener(
        'click',
        () => trackCTAClick('secondary', secondary.href),
        { passive: true }
      );
    }
  }

  function createRippleEffect(event, element) {
    const rect = element.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    const ripple = document.createElement('span');
    ripple.className = 'hero-ripple';
    ripple.style.cssText =
      `position:absolute;top:${y}px;left:${x}px;width:${size}px;height:${size}px;` +
      `background:rgba(255,255,255,.3);border-radius:50%;transform:scale(0);` +
      `animation:hero-ripple .6s linear;pointer-events:none;`;

    if (!document.getElementById('hero-ripple-keyframes')) {
      const style = document.createElement('style');
      style.id = 'hero-ripple-keyframes';
      style.textContent = '@keyframes hero-ripple{to{transform:scale(2);opacity:0}}';
      document.head.appendChild(style);
    }

    const prevPos = getComputedStyle(element).position;
    if (prevPos === 'static') element.style.position = 'relative';
    element.style.overflow = 'hidden';
    element.appendChild(ripple);

    setTimeout(() => {
      ripple.remove();
      if (prevPos === 'static') element.style.position = '';
    }, 650);
  }

  function initKeyboardNavigation(section) {
    section
      .querySelectorAll('.hero-atf__primary-cta, .hero-atf__secondary-cta')
      .forEach((el) => {
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            el.click();
          }
        });
      });
  }

  function trackCTAClick(type, href) {
    if (window.gtag) {
      window.gtag('event', 'click', {
        event_category: 'Hero CTA',
        event_label: type,
        value: href,
      });
    }
  }

  // ---- Breakpoints (rAF-throttled) ---------------------------------------
  function setInitialBreakpoint() {
    const bp = getBreakpoint();
    document.querySelectorAll('.hero-atf').forEach((s) => (s.dataset.breakpoint = bp));
  }

  function getBreakpoint() {
    const w = window.innerWidth;
    return w >= 1024 ? 'desktop' : w >= 768 ? 'tablet' : 'mobile';
  }

  let resizeRAF = null;
  function onResize() {
    if (resizeRAF) return;
    resizeRAF = requestAnimationFrame(() => {
      const bp = getBreakpoint();
      document
        .querySelectorAll('.hero-atf')
        .forEach((s) => (s.dataset.breakpoint = bp));
      resizeRAF = null;
    });
  }
})();
